import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Dropdown, Menu, Tooltip, message, Input, Space, Row, Col, Pagination } from 'antd';
import { MoreOutlined, CopyOutlined, SearchOutlined } from '@ant-design/icons';
import './candidateDataTableStyle.css';
import { useNavigate } from 'react-router-dom';
import apiCall from '../../app/API/apiService';
import apiEndpoints from '../../app/API/apiEndpoints';
import { toasty } from '../../Helper';
import moment from 'moment-timezone';

function CandidateDataTable() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize, setPageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);

  const handleMenuClick = (event, candidate) => {
    setAnchorEl(event.currentTarget);
    setSelectedCandidate(candidate);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCandidate(null);
  };

  const handleEdit = () => {
    // Add edit functionality here
    handleMenuClose();
  };

  const handleDelete = () => {
    // Add delete functionality here
    handleMenuClose();
  };

  const fetchCandidates = useCallback(async () => {
    setIsLoading(true);
    try {
      let url;
      
      if (searchQuery.trim()) {
        // Use search endpoint when there's a search query
        url = `${apiEndpoints.CANDIDATE_SEARCH}?query=${encodeURIComponent(searchQuery.trim())}&page=${page}&page_size=${pageSize}`;
      } else {
        // Use list endpoint when no search
        url = `${apiEndpoints.CANDIDATE_LIST}?page=${page}&page_size=${pageSize}`;
      }
      
      const response = await apiCall("get", url, {});
      
      // Add validation checks
      if (response && response.items && Array.isArray(response.items)) {
        setCandidates(response.items);
        setFilteredCandidates(response.items);
        setTotalPages(Math.ceil(response.total / pageSize));
        setTotalItems(response.total || 0);
      } else {
        console.error('Invalid response format:', response);
        setCandidates([]);
        setFilteredCandidates([]);
        setTotalPages(0);
        setTotalItems(0);
        toasty('Error loading candidates: Invalid data format', 'error');
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setCandidates([]);
      setFilteredCandidates([]);
      setTotalPages(0);
      setTotalItems(0);
      toasty('Error fetching candidates', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCandidates();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, searchQuery, pageSize, fetchCandidates]);

  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text);
    toasty('Copied to clipboard!', 'success');
  };

  const handlePageChange = (newPage) => {
    if (typeof newPage === 'number' && newPage >= 1) {
      setPage(newPage);
      // Reset filtered candidates when changing page
      setFilteredCandidates([]);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleRowClick = (candidate) => {
    navigate(`/candidate-job-openings?candidate_id=${candidate.id}`);
  };

  const handleRowsPerPageChange = (current, size) => {
    setPageSize(size);
    setPage(1);
  };

  const columns = [
    {
      title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>ID</span>,
      dataIndex: "id",
      key: "id",
      render: (text) => (
        <>
          {text.slice(-10)}
          <Tooltip title="Copy ID">
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyText(text);
              }}
              style={{ marginLeft: 8 }}
            />
          </Tooltip>
        </>
      ),
    },
    {
      title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Created At</span>,
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (
        text ? moment.utc(text).tz('Asia/Kolkata').format('DD/MM/YYYY, HH:mm') : 'N/A'
      ),
    },
    {
      title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Organization</span>,
      dataIndex: "organisation_name",
      key: "organisation_name",
      render: (text) => text || 'N/A',
    },
    {
      title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Name</span>,
      dataIndex: "candidate_name",
      key: "candidate_name",
    },
    {
      title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Phone</span>,
      dataIndex: "candidate_phone",
      key: "candidate_phone",
    },
    {
      title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Email</span>,
      dataIndex: "candidate_email",
      key: "candidate_email",
    },
    {
      title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Actions</span>,
      key: "actions",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
             
            </Menu>
          }
          trigger={["click"]}
        >
          <Button 
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="candidate-data-container">
      <div style={{ margin: 10 }}>
        <Space style={{ marginBottom: 16, justifyContent: 'flex-end', width: '100%' }}>
          <Input
            placeholder="Search by name, email or phone..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ width: 300 }}
          />
        </Space>

        <Table
          columns={columns}
          dataSource={Array.isArray(filteredCandidates) ? filteredCandidates : []}
          rowKey="id"
          size="small"
          pagination={false}
          rowClassName="custom-row"
          className="antTableClass"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' }
          })}
          footer={() => (
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              backgroundColor: 'rgba(238, 238, 240, 1)',
              borderTop: '0.5px solid rgba(218, 218, 221, 1)',
              margin: -16, // Negative margin to cover the whole cell
              padding: 16,  // Add padding back to maintain spacing
              width: 'calc(100% + 32px)', // Ensure full width coverage
              borderTop: '1px solid #f0f0f0',
              maxWidth: '100%', // Ensure it doesn't overflow
              overflow: 'hidden' // Prevent overflow
            }}>
              <div style={{ 
                fontFamily: 'Inter', 
                fontSize: '12px', 
                fontWeight: 400, 
                lineHeight: '18px', 
                letterSpacing: '-0.1px', 
                color: '#27272A' 
              }}>
                Showing {Math.min(Array.isArray(filteredCandidates) ? filteredCandidates.length : 0, pageSize)} of {totalItems} Candidates
              </div>

              <div style={{ textAlign: 'center' }}>
                {totalItems > 0 && (
                  <Pagination
                    current={page}
                    total={totalItems}
                    pageSize={pageSize}
                    showSizeChanger={false}
                    size="small"
                    onChange={(newPage) => handlePageChange(newPage)}
                    itemRender={(current, type, originalElement) => {
                      if (type === 'prev' || type === 'next') {
                        return null;
                      }
                      return originalElement;
                    }}
                    className="custom-pagination"
                  />
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <button
                  onClick={() => {
                    if (page > 1) {
                      handlePageChange(page - 1);
                    }
                  }}
                  disabled={page <= 1}
                  style={{ 
                    background: 'none',
                    border: 'none',
                    fontFamily: 'Inter', 
                    fontSize: '12px', 
                    fontWeight: 400, 
                    lineHeight: '18px', 
                    letterSpacing: '-0.1px', 
                    color: page > 1 ? '#666' : '#ccc', 
                    cursor: page > 1 ? 'pointer' : 'not-allowed', 
                    padding: '0 16px 0 0'
                  }}
                >
                  ← Previous
                </button>
                <div 
                  style={{ 
                    borderLeft: '1px solid #d9d9d9',
                    margin: '0 8px',
                    height: '20px'
                  }}
                />
                <button
                  onClick={() => {
                    const maxPage = Math.ceil(totalItems / pageSize);
                    if (page < maxPage) {
                      handlePageChange(page + 1);
                    }
                  }}
                  disabled={page >= Math.ceil(totalItems / pageSize)}
                  style={{ 
                    background: 'none',
                    border: 'none',
                    fontFamily: 'Inter', 
                    fontSize: '12px', 
                    fontWeight: 400, 
                    lineHeight: '18px', 
                    letterSpacing: '-0.1px', 
                    color: page < Math.ceil(totalItems / pageSize) ? '#666' : '#ccc', 
                    cursor: page < Math.ceil(totalItems / pageSize) ? 'pointer' : 'not-allowed',
                    padding: '0 0 0 8px'
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

export default CandidateDataTable; 